/*
 * Copyright (c) 2024-2026 Nodals AI Ltd
 * All rights reserved.
 */

/**
 * Whether a slot-advertised size is a renderable banner size, and so belongs
 * in supportedSizes and the placement.sizes fact. Deliberately NOT an IAB
 * allowlist: publishers traffic direct-sold custom sizes (970x251 roadblocks,
 * 428x150 mobile formats), and management-api imports those sizes into
 * ad-level placement.sizes rules — an allowlist here silently made every
 * campaign whose only creative is a custom size permanently ineligible.
 * 1x1 is excluded because it conventionally marks native capability, not a
 * renderable banner (the GPT slice maps it to supportsNative).
 */
const isRenderableBannerSize = (width: unknown, height: unknown): boolean =>
  typeof width === 'number' &&
  typeof height === 'number' &&
  Number.isInteger(width) &&
  Number.isInteger(height) &&
  width > 1 &&
  height > 1;

const DIMENSIONS_UNKNOWN = '-1x-1';

// Pseudo-size token in the placement.sizes fact for placements that accept
// native creatives (prebid native mediaType, or a GPT slot declaring 'fluid'
// or 1x1). Native has no width/height, so without this token native-capable
// placements are invisible to size-based targeting rules.
const NATIVE_SIZE_TOKEN = 'native';

// Video viewable impression: continuous in-view playback required before dispatch
const VIEWABLE_IMPRESSION_PLAYBACK_MS = 2000;
const VIEWABILITY_CHECK_INTERVAL_MS = 500;

enum Visibility {
  UNKNOWN = -1,
  NOT_VISIBLE = 0,
  VISIBLE = 1,
}

enum PlacementType {
  DISPLAY = 'd',
  VIDEO = 'v',
  ADVERTORIAL = 'a',
}

// Bounded probe for a native slot that was absent at placement init
const SLOT_DETECTION_MAX_ATTEMPTS = 6;
const SLOT_DETECTION_INTERVAL_MS = 500;

// String-valued so the state reads directly in logs and debug pixels
enum SlotDiscoveryState {
  PENDING = 'pending',
  DISCOVERED = 'discovered',
  FAILED = 'failed',
}

export {
  DIMENSIONS_UNKNOWN,
  isRenderableBannerSize,
  NATIVE_SIZE_TOKEN,
  PlacementType,
  SLOT_DETECTION_INTERVAL_MS,
  SLOT_DETECTION_MAX_ATTEMPTS,
  SlotDiscoveryState,
  VIEWABILITY_CHECK_INTERVAL_MS,
  VIEWABLE_IMPRESSION_PLAYBACK_MS,
  Visibility,
};
